import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/auth';
import {
  DollarSign,
  Check,
  X,
  Clock,
  Send,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  FileText,
  Download,
} from 'lucide-react';

const providerLogos: Record<string, string> = {
  paypal: '/images/providers/paypal.png',
  wise: '/images/providers/wise.svg',
  stripe: '/images/providers/stripe.png',
  binance: '/images/providers/binance.png',
};

const providerBgColors: Record<string, string> = {
  paypal: '#ffffff',
  wise: '#f0fbe8',
  stripe: '#f5f5ff',
  binance: '#1E2329',
};

const providerNames: Record<string, string> = {
  paypal: 'PayPal',
  wise: 'Wise',
  stripe: 'Stripe',
  binance: 'Binance Pay',
};
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Table from '../components/ui/Table';
import StatCard from '../components/ui/StatCard';

interface PayoutRequest {
  id: string;
  affiliateId: string;
  amount: string | number;
  currency: string;
  status: string;
  requestedAt: string;
  approvedAt: string | null;
  processedAt: string | null;
  completedAt: string | null;
  failureReason: string | null;
  notes: string | null;
  affiliate: {
    id: string;
    name: string;
    email: string;
  };
  paymentMethod: {
    provider: string;
    accountId: string;
    accountName: string | null;
  };
  batch: {
    id: string;
    status: string;
    providerBatchId: string | null;
  } | null;
  transaction: {
    status: string;
    fee: string | number;
    netAmount: string | number;
    providerStatus: string | null;
    errorMessage: string | null;
  } | null;
  invoice: {
    id: string;
    invoiceNumber: string;
    status: string;
  } | null;
}

interface PayoutStats {
  pending: { count: number; amount: number };
  processing: { count: number; amount: number };
  completed: { count: number; amount: number };
  failed: { count: number; amount: number };
  totalPaid: number;
}

interface PayoutBatch {
  id: string;
  batchId: string;
  totalAmount: string | number;
  currency: string;
  itemCount: number;
  status: string;
  providerBatchId: string | null;
  createdAt: string;
  provider: {
    provider: string;
  };
  _count: {
    payoutRequests: number;
    transactions: number;
  };
}

export default function PayoutManagement() {
  const { accessToken } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'requests' | 'batches'>('requests');
  const [requests, setRequests] = useState<PayoutRequest[]>([]);
  const [batches, setBatches] = useState<PayoutBatch[]>([]);
  const [stats, setStats] = useState<PayoutStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState<string>('pending');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<{ id: string; open: boolean }>({ id: '', open: false });
  const [rejectReason, setRejectReason] = useState('');
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingProvider, setSyncingProvider] = useState<string | null>(null);
  const [syncDropdownOpen, setSyncDropdownOpen] = useState(false);
  const syncDropdownRef = useRef<HTMLDivElement>(null);

  const API_BASE = import.meta.env.VITE_API_URL || '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (syncDropdownRef.current && !syncDropdownRef.current.contains(event.target as Node)) {
        setSyncDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchData();
  }, [filter, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        'X-Tenant-ID': 'demo-casino',
      };
      const [statsRes, requestsRes, batchesRes] = await Promise.all([
        fetch(`${API_BASE}/api/v1/payment-providers/admin/stats`, { headers }),
        fetch(`${API_BASE}/api/v1/payment-providers/admin/requests?status=${filter}`, { headers }),
        fetch(`${API_BASE}/api/v1/payment-providers/admin/batches`, { headers }),
      ]);

      const [statsData, requestsData, batchesData] = await Promise.all([
        statsRes.json(),
        requestsRes.json(),
        batchesRes.json(),
      ]);

      if (statsData.success) setStats(statsData.data);
      if (requestsData.success) setRequests(requestsData.data);
      if (batchesData.success) setBatches(batchesData.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/admin/requests/${id}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });
      const data = await response.json();
      if (data.success) {
        fetchData();
      } else {
        alert(data.error || 'Failed to approve');
      }
    } catch (error) {
      console.error('Failed to approve:', error);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.id || !rejectReason) return;

    try {
      const response = await fetch(
        `${API_BASE}/api/v1/payment-providers/admin/requests/${rejectModal.id}/reject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            'X-Tenant-ID': 'demo-casino',
          },
          body: JSON.stringify({ reason: rejectReason }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setRejectModal({ id: '', open: false });
        setRejectReason('');
        fetchData();
      } else {
        alert(data.error || 'Failed to reject');
      }
    } catch (error) {
      console.error('Failed to reject:', error);
    }
  };

  const handleProcess = async (id: string) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/admin/requests/${id}/process`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });
      const data = await response.json();
      if (data.success) {
        fetchData();
      } else {
        alert(data.error || 'Failed to process');
      }
    } catch (error) {
      console.error('Failed to process:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleBatchProcess = async () => {
    if (selectedRequests.length === 0) return;

    setProcessing(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/admin/process-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
        body: JSON.stringify({ payoutRequestIds: selectedRequests }),
      });
      const data = await response.json();
      if (data.success) {
        setSelectedRequests([]);
        fetchData();
      } else {
        alert(data.error || 'Failed to process batch');
      }
    } catch (error) {
      console.error('Failed to process batch:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleSync = async (id: string) => {
    setSyncing(id);
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/admin/requests/${id}/sync`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });
      const data = await response.json();
      if (data.success) {
        fetchData();
      } else {
        alert(data.error || 'Failed to sync');
      }
    } catch (error) {
      console.error('Failed to sync:', error);
    } finally {
      setSyncing(null);
    }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    setSyncDropdownOpen(false);
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/admin/sync-all`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });
      const data = await response.json();
      if (data.success) {
        alert(`Synced: ${data.data.synced}, Failed: ${data.data.failed}`);
        fetchData();
      } else {
        alert(data.error || 'Failed to sync all');
      }
    } catch (error) {
      console.error('Failed to sync all:', error);
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSyncByProvider = async (provider: string) => {
    setSyncingProvider(provider);
    setSyncDropdownOpen(false);
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/admin/sync-provider/${provider}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });
      const data = await response.json();
      if (data.success) {
        alert(`${providerNames[provider]} - Synced: ${data.data.synced}, Failed: ${data.data.failed}`);
        fetchData();
      } else {
        alert(data.error || `Failed to sync ${providerNames[provider]}`);
      }
    } catch (error) {
      console.error(`Failed to sync ${provider}:`, error);
    } finally {
      setSyncingProvider(null);
    }
  };

  const handleDownloadInvoice = async (invoiceId: string, invoiceNumber: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/invoices/${invoiceId}/download`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download invoice');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download invoice:', error);
      alert('Failed to download invoice');
    }
  };

  const handleGenerateInvoice = async (payoutRequestId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/invoices/generate/${payoutRequestId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });
      const data = await response.json();
      if (data.success) {
        alert(`Invoice ${data.data.invoiceNumber} generated successfully`);
        fetchData();
      } else {
        alert(data.error || 'Failed to generate invoice');
      }
    } catch (error) {
      console.error('Failed to generate invoice:', error);
      alert('Failed to generate invoice');
    }
  };

  const toggleSelectAll = () => {
    if (selectedRequests.length === requests.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(requests.filter((r) => r.status === 'approved').map((r) => r.id));
    }
  };

  const toggleSelect = (id: string) => {
    if (selectedRequests.includes(id)) {
      setSelectedRequests(selectedRequests.filter((i) => i !== id));
    } else {
      setSelectedRequests([...selectedRequests, id]);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'warning' | 'info' | 'success' | 'danger' | 'secondary'> = {
      pending: 'warning',
      approved: 'info',
      processing: 'info',
      completed: 'success',
      failed: 'danger',
      cancelled: 'secondary',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const formatAmount = (amount: string | number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(typeof amount === 'string' ? parseFloat(amount) : amount);
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Payout Management</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Manage affiliate payout requests and batches.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-2">
          <Button variant="secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          {/* Sync Dropdown */}
          <div className="relative" ref={syncDropdownRef}>
            <Button
              variant="outline"
              onClick={() => setSyncDropdownOpen(!syncDropdownOpen)}
              disabled={syncingAll || !!syncingProvider}
            >
              {syncingAll || syncingProvider ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {syncingProvider ? `Syncing ${providerNames[syncingProvider]}...` : 'Sync Providers'}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>

            {syncDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg z-50">
                <div className="py-1">
                  <button
                    onClick={handleSyncAll}
                    className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-3"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Sync All Providers
                  </button>
                  <div className="border-t border-[var(--border-color)] my-1" />
                  <button
                    onClick={() => handleSyncByProvider('paypal')}
                    className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-3"
                  >
                    <div
                      className="w-5 h-5 rounded flex items-center justify-center p-0.5"
                      style={{ backgroundColor: providerBgColors['paypal'] }}
                    >
                      <img src={providerLogos['paypal']} alt="PayPal" className="w-full h-full object-contain" />
                    </div>
                    Sync PayPal Only
                  </button>
                  <button
                    onClick={() => handleSyncByProvider('wise')}
                    className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-3"
                  >
                    <div
                      className="w-5 h-5 rounded flex items-center justify-center p-0.5"
                      style={{ backgroundColor: providerBgColors['wise'] }}
                    >
                      <img src={providerLogos['wise']} alt="Wise" className="w-full h-full object-contain" />
                    </div>
                    Sync Wise Only
                  </button>
                </div>
              </div>
            )}
          </div>

          {selectedRequests.length > 0 && (
            <Button variant="primary" onClick={handleBatchProcess} disabled={processing}>
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Process Selected ({selectedRequests.length})
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Pending"
            value={formatAmount(stats.pending.amount)}
            subtitle={`${stats.pending.count} requests`}
            icon={<Clock className="w-6 h-6" />}
            iconColor="text-yellow-400"
          />
          <StatCard
            title="Processing"
            value={formatAmount(stats.processing.amount)}
            subtitle={`${stats.processing.count} requests`}
            icon={<Loader2 className="w-6 h-6" />}
            iconColor="text-blue-400"
          />
          <StatCard
            title="Completed"
            value={formatAmount(stats.completed.amount)}
            subtitle={`${stats.completed.count} requests`}
            icon={<CheckCircle2 className="w-6 h-6" />}
            iconColor="text-green-400"
          />
          <StatCard
            title="Failed"
            value={formatAmount(stats.failed.amount)}
            subtitle={`${stats.failed.count} requests`}
            icon={<XCircle className="w-6 h-6" />}
            iconColor="text-red-400"
          />
          <StatCard
            title="Total Paid"
            value={formatAmount(stats.totalPaid)}
            subtitle="All time"
            icon={<DollarSign className="w-6 h-6" />}
            iconColor="text-primary-400"
          />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-[var(--border-color)]">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('requests')}
            className={`pb-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'requests'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            Payout Requests
          </button>
          <button
            onClick={() => setActiveTab('batches')}
            className={`pb-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'batches'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            Batches
          </button>
        </nav>
      </div>

      {/* Filters */}
      {activeTab === 'requests' && (
        <div className="flex gap-2">
          {['pending', 'approved', 'processing', 'completed', 'failed'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-primary-500 text-white'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : activeTab === 'requests' ? (
        <Card>
          <Table
            columns={[
              {
                header: (
                  <input
                    type="checkbox"
                    checked={selectedRequests.length > 0 && selectedRequests.length === requests.filter(r => r.status === 'approved').length}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                ),
                accessor: (row: PayoutRequest) => (
                  row.status === 'approved' ? (
                    <input
                      type="checkbox"
                      checked={selectedRequests.includes(row.id)}
                      onChange={() => toggleSelect(row.id)}
                      className="rounded"
                    />
                  ) : null
                ),
              },
              { header: 'Affiliate', accessor: (row: PayoutRequest) => (
                <div>
                  <div className="font-medium text-[var(--text-primary)]">{row.affiliate.name}</div>
                  <div className="text-sm text-[var(--text-muted)]">{row.affiliate.email}</div>
                </div>
              )},
              { header: 'Amount', accessor: (row: PayoutRequest) => (
                <div className="font-semibold text-[var(--text-primary)]">
                  {formatAmount(row.amount)}
                </div>
              )},
              { header: 'Method', accessor: (row: PayoutRequest) => (
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center p-1.5"
                    style={{ backgroundColor: providerBgColors[row.paymentMethod.provider] || '#ffffff' }}
                  >
                    <img
                      src={providerLogos[row.paymentMethod.provider]}
                      alt={row.paymentMethod.provider}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                  <div>
                    <div className="font-medium text-[var(--text-primary)]">{providerNames[row.paymentMethod.provider] || row.paymentMethod.provider}</div>
                    <div className="text-xs text-[var(--text-muted)]">{row.paymentMethod.accountId}</div>
                  </div>
                </div>
              )},
              { header: 'Status', accessor: (row: PayoutRequest) => getStatusBadge(row.status) },
              { header: 'Requested', accessor: (row: PayoutRequest) => (
                <span className="text-sm text-[var(--text-secondary)]">{formatDate(row.requestedAt)}</span>
              )},
              { header: 'Invoice', accessor: (row: PayoutRequest) => (
                row.status === 'completed' ? (
                  row.invoice ? (
                    <button
                      onClick={() => handleDownloadInvoice(row.invoice!.id, row.invoice!.invoiceNumber)}
                      className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-primary-400 bg-primary-500/10 hover:bg-primary-500/20 rounded-lg transition-colors"
                      title={`Download ${row.invoice.invoiceNumber}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                      {row.invoice.invoiceNumber}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleGenerateInvoice(row.id)}
                      className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-yellow-400 bg-yellow-500/10 hover:bg-yellow-500/20 rounded-lg transition-colors"
                      title="Generate Invoice"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Generate
                    </button>
                  )
                ) : (
                  <span className="text-xs text-[var(--text-muted)]">-</span>
                )
              )},
              { header: 'Actions', accessor: (row: PayoutRequest) => (
                <div className="flex gap-2">
                  {row.status === 'pending' && (
                    <>
                      <Button variant="success" size="sm" onClick={() => handleApprove(row.id)}>
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setRejectModal({ id: row.id, open: true })}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                  {row.status === 'approved' && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleProcess(row.id)}
                      disabled={processing}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Process
                    </Button>
                  )}
                  {row.status === 'processing' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleSync(row.id)}
                      disabled={syncing === row.id}
                    >
                      {syncing === row.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3 h-3 mr-1" />
                      )}
                      Sync
                    </Button>
                  )}
                  <button
                    onClick={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                    className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  >
                    {expandedRow === row.id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                </div>
              )},
            ]}
            data={requests}
            renderExpandedRow={expandedRow ? (row: PayoutRequest) => (
              row.id === expandedRow ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 bg-[var(--bg-tertiary)]">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-[var(--text-muted)]">Approved:</span>
                        <span className="ml-2 text-[var(--text-primary)]">{formatDate(row.approvedAt)}</span>
                      </div>
                      <div>
                        <span className="text-[var(--text-muted)]">Processed:</span>
                        <span className="ml-2 text-[var(--text-primary)]">{formatDate(row.processedAt)}</span>
                      </div>
                      <div>
                        <span className="text-[var(--text-muted)]">Completed:</span>
                        <span className="ml-2 text-[var(--text-primary)]">{formatDate(row.completedAt)}</span>
                      </div>
                      {row.transaction && (
                        <>
                          <div>
                            <span className="text-[var(--text-muted)]">Fee:</span>
                            <span className="ml-2 text-[var(--text-primary)]">{formatAmount(row.transaction.fee)}</span>
                          </div>
                          <div>
                            <span className="text-[var(--text-muted)]">Net Amount:</span>
                            <span className="ml-2 text-[var(--text-primary)]">{formatAmount(row.transaction.netAmount)}</span>
                          </div>
                          <div>
                            <span className="text-[var(--text-muted)]">Provider Status:</span>
                            <span className="ml-2 text-[var(--text-primary)]">{row.transaction.providerStatus || '-'}</span>
                          </div>
                        </>
                      )}
                      {row.failureReason && (
                        <div className="col-span-3">
                          <span className="text-red-400">Error: {row.failureReason}</span>
                        </div>
                      )}
                      {row.notes && (
                        <div className="col-span-3">
                          <span className="text-[var(--text-muted)]">Notes:</span>
                          <span className="ml-2 text-[var(--text-primary)]">{row.notes}</span>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ) : null
            ) : undefined}
          />
          {requests.length === 0 && (
            <div className="text-center py-12 text-[var(--text-muted)]">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No payout requests found</p>
            </div>
          )}
        </Card>
      ) : (
        <Card>
          <Table
            columns={[
              { header: 'Batch ID', accessor: (row: PayoutBatch) => (
                <div>
                  <div className="font-mono text-sm text-[var(--text-primary)]">{row.batchId.slice(0, 20)}...</div>
                  {row.providerBatchId && (
                    <div className="text-xs text-[var(--text-muted)]">Provider: {row.providerBatchId}</div>
                  )}
                </div>
              )},
              { header: 'Provider', accessor: (row: PayoutBatch) => (
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center p-1.5"
                    style={{ backgroundColor: providerBgColors[row.provider.provider] || '#ffffff' }}
                  >
                    <img
                      src={providerLogos[row.provider.provider]}
                      alt={row.provider.provider}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                  <span className="font-medium text-[var(--text-primary)]">
                    {providerNames[row.provider.provider] || row.provider.provider}
                  </span>
                </div>
              )},
              { header: 'Total Amount', accessor: (row: PayoutBatch) => (
                <span className="font-semibold text-[var(--text-primary)]">{formatAmount(row.totalAmount)}</span>
              )},
              { header: 'Items', accessor: (row: PayoutBatch) => (
                <span className="text-[var(--text-secondary)]">{row.itemCount}</span>
              )},
              { header: 'Status', accessor: (row: PayoutBatch) => getStatusBadge(row.status) },
              { header: 'Created', accessor: (row: PayoutBatch) => (
                <span className="text-sm text-[var(--text-secondary)]">{formatDate(row.createdAt)}</span>
              )},
            ]}
            data={batches}
          />
          {batches.length === 0 && (
            <div className="text-center py-12 text-[var(--text-muted)]">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No batches found</p>
            </div>
          )}
        </Card>
      )}

      {/* Reject Modal */}
      {rejectModal.open && (
        <div className="modal-overlay" onClick={() => setRejectModal({ id: '', open: false })}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-[var(--border-color)]">
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">Reject Payout Request</h2>
            </div>
            <div className="p-6">
              <label className="label">Rejection Reason</label>
              <textarea
                className="input min-h-[100px]"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
              />
            </div>
            <div className="px-6 py-4 border-t border-[var(--border-color)] flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setRejectModal({ id: '', open: false })}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleReject} disabled={!rejectReason}>
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
